import { config } from '@/lib/helpers/config.helpers'
import { createTokenRefreshInterceptor } from '@/lib/utils/create-token-refresh-interceptor'
import { notifyUserBanned } from '@/services/banned-user.service'
import axios from 'axios'

const httpClient = axios.create({
  baseURL: config.apiBaseUrl,
})

export const financeHttpClient = axios.create({
  baseURL: config.financeServiceApiBaseUrl,
})

export default httpClient

export const setupInterceptors = (store) => {
  const applyInterceptors = (client) => {
    client.interceptors.request.use(
      (config) => {
        const authorization = store.getters['auth/authorization']
        if (authorization) config.headers['Authorization'] = authorization
        return config
      },
      (error) => Promise.reject(error),
    )

    client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
          notifyUserBanned()
        }

        return Promise.reject(error)
      },
    )

    createTokenRefreshInterceptor(client, {
      refreshToken: () => store.dispatch('auth/refreshToken'),
      onRefreshFailure: () => {
        store.dispatch('auth/logout').then((logoutUrl) => {
          if (logoutUrl) {
            window.location.href = logoutUrl
          }
        })
      },
    })
  }

  applyInterceptors(httpClient)
  applyInterceptors(financeHttpClient)
}

const contentTypeExtensions = {
  'application/zip': 'zip',
  'application/x-zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'application/xml': 'xml',
  'text/xml': 'xml',
}

export const saveBlobResponse = (response, url) => {
  const blob = response.data

  let filename
  const contentDisposition = response.headers['content-disposition']
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8|utf-8)''(.+?)(?:;|$)/)
    if (utf8Match) {
      filename = decodeURIComponent(utf8Match[1])
    } else {
      const match = contentDisposition.match(/filename=((['"]).*?\2|[^;\n]*)/)
      if (match) filename = match[1].replace(/['"]/g, '')
    }
  }

  if (!filename) {
    const baseName = url.split('?')[0].split('/').filter(Boolean).pop() || 'download'
    const contentType = response.headers['content-type']?.split(';')[0]?.trim()
    const ext = contentTypeExtensions[contentType] || 'bin'
    filename = `${baseName}.${ext}`
  }

  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

export const downloadFile = async (client, url, data = {}) => {
  const params = new URLSearchParams(data)
  const response = await client.post(url, params, { responseType: 'blob' })
  saveBlobResponse(response, url)
}

export const uploadFileAndDownloadResponse = async (client, url, formData) => {
  const response = await client.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  })
  saveBlobResponse(response, url)
}
